import { Injectable } from '@angular/core';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { PropertyManagementCompany } from '../../models/entities/property-management-company';
import { BehaviorSubject, combineLatest, filter, Observable, of, Subject } from 'rxjs';
import { PropertyManagementCompanies } from '../../components/shared/http/request-interfaces';
import { GrowlerService } from '../../components/shared/growler/growler.service';

@Injectable({
    providedIn: 'root',
})
export class PropertyManagementCompanyService
{
    public readonly pmcId$ = new BehaviorSubject<number | null>(null);
    public readonly clear$ = new Subject<void>();

    public readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    public readonly updating$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public readonly propertyManagementCompany$: Observable<PropertyManagementCompany> = combineLatest([
        this.pmcId$.pipe(distinctUntilChanged()),
        this.clear$.pipe(startWith(undefined)),
    ])
            .pipe(
                    map(([pmcId]) => pmcId),
                    filter((pmcId) => !!pmcId),
                    tap(() => this.loading$.next(true)),
                    switchMap(pmcId => this.getPropertyManagementCompany(pmcId)),
                    catchError((err) => {
                        this._growler.fromErrorResponse(err);
                        return of(null);
                    }),
                    tap(() => this.loading$.next(false)),
                    shareReplay({bufferSize: 1, refCount: false}),
            );

    constructor(
            private _http: ApplicationHttpClient,
            private _growler: GrowlerService,
    ) {
    }

    setPmcId(pmcId: number): void {
        this.pmcId$.next(pmcId);
    }

    public refresh(): void {
        this.clear$.next();
    }

    public getPropertyManagementCompany(
            pmcId: number,
            withs: string[] = [],
    ) {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.PMCV2Module.PropertyManagementCompany(pmcId) + (withs.length ? '?with=' + withs.join(',') : ''),
        );

        return this._http.get<PropertyManagementCompany>(url).pipe(map((res: any) => new PropertyManagementCompany(res)));
    }


    /**
     * Updates pmc details from Core.
     * @param pmcId
     * @param requestBody
     * @returns {Observable<Organization>}
     */
    public updatePropertyManagementCompany(
            pmcId: number,
            requestBody: PropertyManagementCompanies.PropertyManagementCompanies.UpdatePropertyManagementCompanyRequest,
    ): Observable<PropertyManagementCompany> {
        this.updating$.next(true);
        return this._http.patch<PropertyManagementCompany>(
            Routes.MakeLegFiCoreUrl(
                    Routes.PMCV2Module.PropertyManagementCompany(pmcId),
            ),
            JSON.stringify(requestBody),
        ).pipe(
                catchError((err) => {
                    this._growler.fromErrorResponse(err);
                    this.updating$.next(false);
                    return of(null);
                }),
                map((response: Object) => {
                    return new PropertyManagementCompany(response);
                }),
                tap(() => {
                    this.updating$.next(false);
                    this.clear$.next();
                }),
        );
    }
}
